import { Loader } from "@circle-react-uikit/Loader";
import type { SelectProps } from "@circle-react-uikit/Select";
import { Select } from "@circle-react-uikit/Select";
import { useCommunityMemberSearch } from "@circle-react/hooks/useCommunityMemberSearch";
import type { CommunityMember } from "@circle-react/types";
import { compact, flatten } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";

import { MemberListItem } from "./MemberListItem";
import { formatCommunityMemberData, getAddableOption } from "./utils";

export interface CommunityMemberSelectProps extends SelectProps {
  excludeAdmins?: boolean;
  filters?: Record<string, any>;
  getCommunityMemberText?: (communityMember: CommunityMember) => string;
  includeInactive?: boolean;
  maxPreloadValues?: number;
  valueField?: "id" | "user_id";
}

const i18nRoot = "community_member_select";

export const CommunityMemberSelect = ({
  valueField = "id",
  addable,
  placeholder,
  excludeAdmins = false,
  includeInactive = false,
  filters = {},
  shouldFilterSelectedItems = false,
  getCommunityMemberText = communityMember => `${communityMember.name}`,
  maxPreloadValues,
  ...passThroughProps
}: CommunityMemberSelectProps) => {
  const { searchCommunityMembers, communityMembers, resetCommunityMembers } =
    useCommunityMemberSearch();

  const { value, defaultValue } = passThroughProps;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const preloadValues = compact(flatten([defaultValue, value]));
    if (preloadValues.length) {
      // If we have 70k members and we try to search for all of them, the request will fail
      // because the query will be too long.
      // So we limit the number of members to search for to 1000.
      // See https://circleso.slack.com/archives/C010C760YAY/p1760123428405739
      const valueFieldValues = maxPreloadValues
        ? preloadValues.slice(0, maxPreloadValues)
        : preloadValues;
      searchCommunityMembers({
        filters: {
          [valueField]: valueFieldValues,
          exclude_admins: excludeAdmins,
          include_inactive: includeInactive,
          ...filters,
        },
      })
        .then(() => setIsLoading(false))
        .catch(error => {
          console.error(error);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const data = useMemo(() => {
    const memberOptions = formatCommunityMemberData(
      communityMembers,
      valueField,
      getCommunityMemberText,
    ).map(({ value, text, member }) => ({
      value,
      text,
      listItemText: <MemberListItem member={member} />,
    }));

    return memberOptions;
  }, [communityMembers, valueField, getCommunityMemberText]);

  if (isLoading) {
    return <Loader />;
  }

  const onSearch = async (query: any) => {
    if (!query) {
      resetCommunityMembers();
      return;
    }
    return searchCommunityMembers({
      filters: {
        exclude_admins: excludeAdmins,
        include_inactive: includeInactive,
        ...filters,
      },
      per_page: 10,
      query,
    });
  };

  const notFoundContent = addable
    ? t([i18nRoot, "no_matches_found_try_using_email"])
    : t([i18nRoot, "no_matches_found"]);

  const placeholderWithFallback =
    placeholder ?? t([i18nRoot, "start_typing_a_name"]);

  return (
    <Select
      {...passThroughProps}
      notFoundContent={notFoundContent}
      addable={addable ? getAddableOption : undefined}
      data={data}
      onSearch={onSearch}
      minSearchValueLength={1}
      placeholder={placeholderWithFallback}
      shouldFilterSelectedItems={shouldFilterSelectedItems}
      popperOptions={{
        modifiers: [{ name: "offset", options: { offset: [0, 2] } }],
        ...passThroughProps.popperOptions,
      }}
    />
  );
};
